import { call, delay, put, race, select, take, takeEvery } from 'redux-saga/effects';
import isEqual from 'lodash/isEqual';

import {
    Platform,
    selectInterfaceLanguageCode,
    selectIsWebSDK,
    selectLegacyPlatform,
    selectPreferredInterfaceLanguage,
    selectTargetPlatform,
} from '@pressreader/appcore';
import { dispatch, selectTyped } from '@pressreader/appstore';
import { error } from '@pressreader/logger';
import { navigateBegan } from '@pressreader/navigation-core';
import { closeContextMenu, openContextMenu, takeCloseContextMenuRequest } from '@pressreader/popups-legacy';
import { Align, IContextMenuPosition } from '@pressreader/popups-types';
import { updateUlrWithLangCode, withAbsolutePath } from '@pressreader/routing';
import { refreshBearerToken, setInterfaceLanguage } from '@pressreader/services';
import { getSponsor } from '@pressreader/sponsorship-core';
import { ExtractReturnType, IFont, TargetPlatform } from '@pressreader/types';
import { localSettings } from '@pressreader/usersettings';

import {
    COOKIE_MULTILANGUAGE_KEY,
    fontsContextMenuName,
    fontTypeContextMenuName,
    interfaceLanguageContextMenuName,
    localSettingsCurrentFontsetName,
    localSettingsCurrentLineSpacingName,
    localSettingsCurrentScaleName,
    TRANSLATE_CONTEXT_MENU_NAME,
} from '../../constants';
import { IFontsContextMenuContext, TranslateMenuContext } from '../../types';
import { validateLineSpacing, validateScale } from '../../utils';
import {
    availableFontsChanged,
    changeFontAction,
    changeFontDone,
    changeFontScaleAction,
    changeInterfaceLanguageAction,
    changeLineSpacingAction,
    decreaseFontScaleAction,
    fontScaleChanged,
    increaseFontScaleAction,
    lineSpacingChanged,
    openChangeInterfaceLanguageMenuAction,
    openFontsMenuAction,
    openFontTypeMenuAction,
    openTranslateMenuAction,
    translateMenuClosed,
    translateMenuOpened,
} from '../actions';
import { selectAvailableFonts, selectCurrentFontId, selectCurrentFontScale, selectCurrentLineSpacing } from '../selectors';

function* openTranslateMenu({ payload }: ReturnType<typeof openTranslateMenuAction>) {
    try {
        const { parentMenuId, center, issueKey, currentLanguageCode } = payload;
        const position: IContextMenuPosition = {
            center: center ?? { x: 0, y: 0 },
            align: center
                ? {
                      y: Align.Center,
                      x: Align.Center,
                  }
                : { x: Align.ScreenCenter, y: Align.ScreenCenter },
        };

        const ctxMenuId = yield* openContextMenu<TranslateMenuContext>({
            parentMenuId,
            name: TRANSLATE_CONTEXT_MENU_NAME,
            context: {
                issueKey,
                currentLanguageCode,
            },
            position,
        });
        yield put(translateMenuOpened());

        const result: { close: ExtractReturnType<typeof takeCloseContextMenuRequest> } = yield race({
            close: takeCloseContextMenuRequest(ctxMenuId),
            navigateBegan: take(navigateBegan),
        });

        yield closeContextMenu(ctxMenuId, { back: result.close?.payload.back || false });

        yield put(translateMenuClosed());
    } catch (err) {
        error(err);
    }
}

function* openChangeInterfaceLanguageMenu(action: ReturnType<typeof openChangeInterfaceLanguageMenuAction>) {
    try {
        const position: IContextMenuPosition = {
            center: action.payload.center,
            align: {
                y: Align.ScreenCenter,
                x: Align.ScreenCenter,
            },
        };

        const ctxMenuId: ExtractReturnType<typeof openContextMenu> = yield openContextMenu<null>({
            parentMenuId: action.payload.parentMenuId,
            name: interfaceLanguageContextMenuName,
            context: null,
            position,
        });

        const { payload }: ExtractReturnType<typeof takeCloseContextMenuRequest> = yield takeCloseContextMenuRequest(ctxMenuId);

        yield closeContextMenu(ctxMenuId, { back: payload.back });

        const languageCode: ReturnType<typeof selectInterfaceLanguageCode> = yield select(selectInterfaceLanguageCode);
        const preferredLanguageCode: ReturnType<typeof selectPreferredInterfaceLanguage> = yield select(selectPreferredInterfaceLanguage);

        window.localStorage.setItem(
            COOKIE_MULTILANGUAGE_KEY,
            JSON.stringify({
                interfaceLangCode: languageCode,
                preferredLanguageCode,
                result: false,
            }),
        );
    } catch (e) {
        error(e);
    }
}

function* changeInterfaceLanguage({ payload }: ReturnType<typeof changeInterfaceLanguageAction>) {
    try {
        const { languageCode } = payload;

        const returnUrl = updateUlrWithLangCode(window.location.pathname + window.location.search, languageCode);
        const platform: ReturnType<typeof selectTargetPlatform> = yield select(selectTargetPlatform);
        const isWebSDK = yield* selectTyped(selectIsWebSDK);

        if (isWebSDK) {
            yield call(setInterfaceLanguage, languageCode);
            yield call(refreshBearerToken, false);

            // refreshBearerToken already awaited above — this delay is a paint budget,
            // not a network barrier. Without it the synchronous window.location.reload()
            // pre-empts React's commit of the language-change spinner state, so the
            // user sees an instant white flash instead of a brief loading indicator.
            yield delay(100);
            yield call([window.location, window.location.reload]);
        } else if (platform !== TargetPlatform.Voyager) {
            yield call(setInterfaceLanguage, languageCode);
            const sponsor: ReturnType<typeof getSponsor> = yield select(getSponsor);
            yield call(refreshBearerToken, !!sponsor?.isGeoLocationValidationEnabled);

            setTimeout(() => {
                window.location.assign(returnUrl);
            }, 30);
        } else {
            const changeLangUrl = withAbsolutePath(`ChangeLanguage?lng=${languageCode}&returnUrl=${encodeURIComponent(returnUrl)}`);

            setTimeout(() => {
                window.location.assign(changeLangUrl);
            }, 30);
        }
    } catch (e) {
        error(e);
    }
}

/**
 * Initial set of available fonts
 * NOTE: for native build there is a different set of available fonts
 */
function* initializeAvailableFonts() {
    let fonts: IFont[];

    const platform: ReturnType<typeof selectLegacyPlatform> = yield select(selectLegacyPlatform);

    switch (platform) {
        case Platform.Android:
            fonts = [
                { id: 'fontset1', name: null },
                { id: 'fontset2', name: 'Sans Serif' },
                { id: 'fontset3', name: 'Palantino' },
                { id: 'fontset4', name: 'Times' },
            ];
            break;
        default:
            fonts = [
                { id: 'fontset1', name: null },
                { id: 'fontset2', name: 'Palantino' },
                { id: 'fontset3', name: 'Times New Roman' },
                { id: 'fontset4', name: 'Helvetica' },
                { id: 'fontset5', name: 'Verdana' },
                { id: 'fontset6', name: 'Georgia' },
                { id: 'fontset-dyslexic', name: 'Dyslexic' },
            ];
            break;
    }

    const availableFonts: ReturnType<typeof selectAvailableFonts> = yield select(selectAvailableFonts);
    if (!isEqual(fonts, availableFonts)) {
        yield put(availableFontsChanged({ fonts }));
    }
}

function* monitorFontChange() {
    const currentFontId: ReturnType<typeof selectCurrentFontId> = yield select(selectCurrentFontId);

    const fontId = localSettings.get(localSettingsCurrentFontsetName, 'fontset1');
    if (currentFontId !== fontId) {
        yield put(changeFontDone({ fontId }));
    }

    localSettings.onSettingsChanged<string>(localSettingsCurrentFontsetName).subscribe(newFontId => {
        dispatch(changeFontDone({ fontId: newFontId }));
    });
}

function* openFontsMenu({ payload }: ReturnType<typeof openFontsMenuAction>) {
    try {
        const position: IContextMenuPosition = payload.center
            ? {
                  center: payload.center,
                  align: {
                      y: Align.Center,
                      x: Align.Center,
                  },
              }
            : { center: { x: 0, y: 0 }, align: { y: Align.ScreenCenter, x: Align.ScreenCenter } };

        const ctxMenuId: ExtractReturnType<typeof openContextMenu> = yield openContextMenu<IFontsContextMenuContext>({
            parentMenuId: payload.parentMenuId,
            name: fontsContextMenuName,
            context: { showFonts: payload.showFonts },
            position,
        });

        const result: { close: ExtractReturnType<typeof takeCloseContextMenuRequest> } = yield race({
            close: takeCloseContextMenuRequest(ctxMenuId),
            navigateBegan: take(navigateBegan),
        });

        yield closeContextMenu(ctxMenuId, { back: result.close?.payload.back || false });
    } catch (e) {
        error(e);
    }
}

function* openFontTypeMenu({ payload }: ReturnType<typeof openFontTypeMenuAction>) {
    try {
        const position: IContextMenuPosition = payload.position ?? {
            center: { x: 0, y: 0 },
            align: { y: Align.ScreenCenter, x: Align.ScreenCenter },
        };

        const ctxMenuId: ExtractReturnType<typeof openContextMenu> = yield openContextMenu<null>({
            parentMenuId: payload.parentMenuId,
            name: fontTypeContextMenuName,
            context: null,
            position,
        });

        const result: { close: ExtractReturnType<typeof takeCloseContextMenuRequest> } = yield race({
            close: takeCloseContextMenuRequest(ctxMenuId),
            navigateBegan: take(navigateBegan),
        });

        yield closeContextMenu(ctxMenuId, { back: result.close?.payload.back || false });
    } catch (e) {
        error(e);
    }
}

function* changeFont({ payload }: ReturnType<typeof changeFontAction>) {
    const currentFontId: ReturnType<typeof selectCurrentFontId> = yield select(selectCurrentFontId);

    if (currentFontId !== payload.fontId) {
        localSettings.set(localSettingsCurrentFontsetName, payload.fontId);
    }
}

function* changeFontScale({ payload }: ReturnType<typeof changeFontScaleAction>) {
    const { fontScale } = payload;
    const scale = validateScale(fontScale);
    const currentFontScale: ReturnType<typeof selectCurrentFontScale> = yield select(selectCurrentFontScale);
    if (scale === currentFontScale) {
        return;
    }
    localSettings.set(localSettingsCurrentScaleName, scale);
    yield put(fontScaleChanged({ fontScale: scale }));
}

function* changeLineSpacing({ payload }: ReturnType<typeof changeLineSpacingAction>) {
    const lineSpacing = validateLineSpacing(payload.lineSpacing);
    const currentLineSpacing: ReturnType<typeof selectCurrentLineSpacing> = yield select(selectCurrentLineSpacing);
    if (lineSpacing === currentLineSpacing) {
        return;
    }
    localSettings.set(localSettingsCurrentLineSpacingName, lineSpacing);
    yield put(lineSpacingChanged({ lineSpacing }));
}

function* increaseFontScale() {
    const currentFontScale: ReturnType<typeof selectCurrentFontScale> = yield select(selectCurrentFontScale);
    yield put(changeFontScaleAction({ fontScale: currentFontScale + 1 }));
}

function* decreaseFontScale() {
    const currentFontScale: ReturnType<typeof selectCurrentFontScale> = yield select(selectCurrentFontScale);
    yield put(changeFontScaleAction({ fontScale: currentFontScale - 1 }));
}

function* saga() {
    yield takeEvery(openChangeInterfaceLanguageMenuAction, openChangeInterfaceLanguageMenu);
    yield takeEvery(changeInterfaceLanguageAction, changeInterfaceLanguage);
    yield takeEvery(openTranslateMenuAction, openTranslateMenu);

    yield initializeAvailableFonts();

    yield monitorFontChange();
    yield takeEvery(openFontsMenuAction, openFontsMenu);
    yield takeEvery(openFontTypeMenuAction, openFontTypeMenu);
    yield takeEvery(changeFontAction, changeFont);

    yield takeEvery(changeFontScaleAction, changeFontScale);
    yield takeEvery(increaseFontScaleAction, increaseFontScale);
    yield takeEvery(decreaseFontScaleAction, decreaseFontScale);
    yield takeEvery(changeLineSpacingAction, changeLineSpacing);
}

export { saga };
